import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  TextField,
  Button,
  Chip,
  Typography,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
} from "@mui/material";
import {
  Add as AddIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Restore as RestoreIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import {
  useTSQLotList,
  useTSQLotListView,
  useTSMDeactivateLot,
  useTSMReactivateLot,
} from "../../hooks/useTSLots";
import { ListRequestFilter } from "../../types/common/commonTypes";
import { InvLotFull, InvLotListView } from "../../types/lotTypes";
import { paginationDefaults } from "../../config/settingsDefaults";
import { useAuth } from "../../contexts/AuthContextWithQuery";

const FilterBox: React.FC<{
  finalSet: React.Dispatch<React.SetStateAction<string>>;
}> = ({ finalSet }) => {
  const [localVal, setLocalVal] = useState<string>("");
  return (
    <TextField
      label="Search"
      value={localVal}
      onChange={(e) => setLocalVal(e.target.value)}
      onBlur={(e) => finalSet(e.target.value)}
      placeholder="Search by lot number, part number, or part name"
      slotProps={{
        input: {
          startAdornment: (
            <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />
          ),
        },
      }}
      sx={{ minWidth: 300 }}
    />
  );
};

export const PageInvLotList: React.FC = () => {
  const navigate = useNavigate();

  const { user } = useAuth();

  // Sorting state

  // Filter state
  const [searchTerm, setSearchTerm] = useState("");
  const [pagination, setPagination] = useState({
    page: 0,
    size: paginationDefaults.startingRowOption,
  });
  const [orderBy, setOrderBy] = useState<string>(
    "inventory_lots.received_date",
  );
  const [order, setOrder] = useState<"asc" | "desc">("desc");

  const [error, setError] = useState<string | null>(null);

  const [recTotal, setRecTotal] = useState<number>(0);
  const [foundLots, setFoundLots] = useState<InvLotListView[]>([]);
  const [shouldSearch, setShouldSearch] = useState<boolean>(true);
  const refShouldSearch = useRef(shouldSearch);

  useEffect(() => {
    refShouldSearch.current = shouldSearch;
  });

  const {
    data: lotsResponse,
    isLoading,
    error: queryError,
    // isPlaceholderData,
  } = useTSQLotListView(
    {
      page: pagination.page,
      page_size: pagination.size,
      sort_by: `${orderBy} ${order.toLocaleUpperCase()}`,
      filter:
        searchTerm === "" || !shouldSearch
          ? null
          : `inventory_lots.lot_number ILIKE '%${searchTerm}%' OR items.number ILIKE '%${searchTerm}%' OR items.name ILIKE '%${searchTerm}%'`,
    },
    shouldSearch,
  );

  useEffect(() => {
    console.log(
      "LOT RESPONSE USE EFFECT",
      refShouldSearch.current,
      lotsResponse,
    );
    if (refShouldSearch.current) {
      if (lotsResponse?.items) {
        setFoundLots(lotsResponse.items);
        setRecTotal(lotsResponse.total);
        setShouldSearch(false);
      }
    }
  }, [lotsResponse]);

  const deactivateLotMutation = useTSMDeactivateLot();
  const reactivateLotMutation = useTSMReactivateLot();

  const handleDeactivate = async (id: string) => {
    if (!window.confirm("Are you sure you want to deactivate this lot?")) {
      return;
    }

    try {
      await deactivateLotMutation.mutateAsync(id);
      setError(null);
    } catch (err: any) {
      console.error("Failed to deactivate lot:", err);
      setError(err.response?.data?.detail || "Failed to deactivate lot");
    }
  };

  const handleReactivate = async (id: string) => {
    try {
      await reactivateLotMutation.mutateAsync(id);
      setError(null);
    } catch (err: any) {
      console.error("Failed to reactivate lot:", err);
      setError(err.response?.data?.detail || "Failed to reactivate lot");
    }
  };

  // Pagination handlers
  const handlePageChange = (event: unknown, newPage: number) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
    setShouldSearch(true);
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setPagination({
      size: parseInt(event.target.value, 10),
      page: 0,
    });
    setShouldSearch(true);
  };

  // Sorting logic
  const handleSort = (property: string) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
    setShouldSearch(true);
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h4" component="h1">
          Lots
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate("/inventory/lots/form?mode=create")}
        >
          Create Lot
        </Button>
      </Box>

      {/* Error Alert */}
      {(error || queryError) && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error || queryError?.message || "An unknown error occurred"}
        </Alert>
      )}

      {/* Loading indicator for background updates */}
      {shouldSearch && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Loading updated data...
        </Alert>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
          <FilterBox finalSet={setSearchTerm} />
          <Button
            variant="contained"
            color="info"
            onClick={() => {
              setShouldSearch(true);
            }}
          >
            Search Lots
          </Button>
        </Box>
      </Paper>

      {/* Data Table */}
      <Paper>
        <TablePagination
          rowsPerPageOptions={paginationDefaults.allRowOptions}
          component="div"
          count={recTotal}
          rowsPerPage={pagination.size}
          page={pagination.page}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell
                  sortDirection={
                    orderBy === "inventory_lots.lot_number" ? order : false
                  }
                >
                  <TableSortLabel
                    active={orderBy === "inventory_lots.lot_number"}
                    direction={
                      orderBy === "inventory_lots.lot_number" ? order : "asc"
                    }
                    onClick={() => handleSort("inventory_lots.lot_number")}
                  >
                    Number
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  sortDirection={orderBy === "items.number" ? order : false}
                >
                  <TableSortLabel
                    active={orderBy === "items.number"}
                    direction={orderBy === "items.number" ? order : "asc"}
                    onClick={() => handleSort("items.number")}
                  >
                    Part
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  sortDirection={
                    orderBy === "inventory_lots.received_date" ? order : false
                  }
                >
                  <TableSortLabel
                    active={orderBy === "inventory_lots.received_date"}
                    direction={
                      orderBy === "inventory_lots.received_date" ? order : "asc"
                    }
                    onClick={() => handleSort("inventory_lots.received_date")}
                  >
                    Received
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  sortDirection={
                    orderBy === "inventory_lots.released_date" ? order : false
                  }
                >
                  <TableSortLabel
                    active={orderBy === "inventory_lots.released_date"}
                    direction={
                      orderBy === "inventory_lots.released_date" ? order : "asc"
                    }
                    onClick={() => handleSort("inventory_lots.released_date")}
                  >
                    Released
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  sortDirection={
                    orderBy === "inventory_lots.expiration_date" ? order : false
                  }
                >
                  <TableSortLabel
                    active={orderBy === "inventory_lots.expiration_date"}
                    direction={
                      orderBy === "inventory_lots.expiration_date"
                        ? order
                        : "asc"
                    }
                    onClick={() => handleSort("inventory_lots.expiration_date")}
                  >
                    Expiration
                  </TableSortLabel>
                </TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {shouldSearch ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : foundLots.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography variant="body1" color="textSecondary">
                      No lots found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                foundLots.map((lot) => {
                  return (
                    <TableRow key={lot.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {lot.lot_number}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Stack direction="column" gap={1}>
                          <Typography variant="body2">
                            {lot.item_num}
                          </Typography>
                          <Typography variant="body2">
                            {lot.item_name}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Stack direction="column" gap={1}>
                          <Typography variant="body2">
                            Date:
                            <br />
                            {new Date(lot.rec_date).toLocaleDateString()}
                          </Typography>
                          <Typography variant="body2">
                            Rev: {lot.init_rev}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        {lot.rel_date ? (
                          <Stack direction="column" gap={1}>
                            <Typography variant="body2">
                              Date:
                              <br />
                              {new Date(lot.rel_date).toLocaleDateString()}
                            </Typography>
                            <Typography variant="body2">
                              Rev: {lot.rel_rev || "-"}
                            </Typography>
                          </Stack>
                        ) : (
                          <Typography variant="body2">Not Released</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {lot.exp_date
                            ? new Date(lot.exp_date).toLocaleDateString()
                            : "-"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1}>
                          <Tooltip title="View Details">
                            <IconButton
                              size="small"
                              onClick={() =>
                                navigate(
                                  `/inventory/lots/form?mode=view&id=${lot.id}`,
                                )
                              }
                            >
                              <ViewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit Lot">
                            <IconButton
                              size="small"
                              onClick={() =>
                                navigate(
                                  `/inventory/lots/form?mode=edit&id=${lot.id}`,
                                )
                              }
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {user?.is_superuser && (
                            <>
                              {!lot.is_record_active ? (
                                <Tooltip title="Reactivate Lot">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleReactivate(lot.id)}
                                    color="success"
                                  >
                                    <RestoreIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              ) : (
                                <Tooltip title="Deactivate Lot">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleDeactivate(lot.id)}
                                    color="error"
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={paginationDefaults.allRowOptions}
          component="div"
          count={recTotal}
          rowsPerPage={pagination.size}
          page={pagination.page}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      </Paper>
    </Box>
  );
};
